import React from 'react';

type CMSImportPhase =
  | 'uploading'
  | 'replicating'
  | 'ready'
  | 'ingesting'
  | 'completed'
  | 'failed';

interface Props {
  phase: CMSImportPhase | string;
  /** If provided, the stepper will show an error state */
  hasError?: boolean;
}

interface Step {
  label: string;
  status: 'complete' | 'current' | 'upcoming' | 'error';
}

const getSteps = (
  phase: CMSImportPhase | string,
  hasError?: boolean,
): Step[] => {
  const steps: Step[] = [
    { label: 'Upload', status: 'upcoming' },
    { label: 'Processing', status: 'upcoming' },
    { label: 'Review', status: 'upcoming' },
    { label: 'Ingest', status: 'upcoming' },
    { label: 'Complete', status: 'upcoming' },
  ];

  // Determine which step to show as error based on current phase
  const getErrorStepIndex = (): number => {
    switch (phase) {
      case 'uploading':
        return 1; // Error during processing
      case 'replicating':
        return 1; // Error during processing
      case 'ready':
        return 2; // Error during review
      case 'ingesting':
        return 3; // Error during ingest
      default:
        return 1;
    }
  };

  switch (phase) {
    case 'uploading':
      steps[0].status = 'complete';
      steps[1].status = hasError ? 'error' : 'current';
      break;
    case 'replicating':
      steps[0].status = 'complete';
      steps[1].status = hasError ? 'error' : 'current';
      break;
    case 'ready':
      steps[0].status = 'complete';
      steps[1].status = 'complete';
      steps[2].status = hasError ? 'error' : 'current';
      break;
    case 'ingesting':
      steps[0].status = 'complete';
      steps[1].status = 'complete';
      steps[2].status = 'complete';
      steps[3].status = hasError ? 'error' : 'current';
      break;
    case 'completed':
      steps[0].status = 'complete';
      steps[1].status = 'complete';
      steps[2].status = 'complete';
      steps[3].status = 'complete';
      steps[4].status = 'complete';
      break;
    case 'failed': {
      // For failed phase, mark steps up to the error point and show error
      const errorIndex = getErrorStepIndex();
      for (let i = 0; i < errorIndex; i += 1) {
        steps[i].status = 'complete';
      }
      steps[errorIndex].status = 'error';
      break;
    }
    default:
      // For unknown phases, just show upload as complete
      steps[0].status = 'complete';
      steps[1].status = hasError ? 'error' : 'current';
  }

  return steps;
};

export const CMSImportStepper: React.FC<Props> = ({ phase, hasError }) => {
  const steps = getSteps(phase, hasError);

  return (
    <nav aria-label="Import Progress">
      <ol className="flex items-center">
        {steps.map((step, index) => (
          <li
            key={step.label}
            className={`relative ${index !== steps.length - 1 ? 'flex-1' : ''}`}
          >
            <div className="flex items-center">
              {/* Step circle */}
              <div className="relative flex items-center justify-center">
                {/* Glow effect for error state */}
                {step.status === 'error' && (
                  <div className="absolute inset-0 rounded-full bg-red-500/20 blur-md scale-150 animate-pulse" />
                )}
                <div
                  className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-300 ease-out ${
                    step.status === 'complete'
                      ? 'border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500'
                      : step.status === 'current'
                        ? 'border-blue-600 bg-white dark:border-blue-500 dark:bg-zinc-900'
                        : step.status === 'error'
                          ? 'border-red-500 bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/30 dark:shadow-red-500/20'
                          : 'border-gray-300 bg-white dark:border-zinc-600 dark:bg-zinc-900'
                  }`}
                >
                  {step.status === 'complete' && (
                    <svg
                      className="h-4 w-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {step.status === 'current' && (
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-600 dark:bg-blue-500" />
                  )}
                  {step.status === 'error' && (
                    <svg
                      className="h-4 w-4 text-white drop-shadow-sm"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Step label */}
              <span
                className={`ml-3 text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  step.status === 'complete'
                    ? 'text-gray-900 dark:text-white'
                    : step.status === 'current'
                      ? 'text-blue-600 dark:text-blue-400'
                      : step.status === 'error'
                        ? 'text-red-500 font-semibold'
                        : 'text-gray-500 dark:text-zinc-400'
                }`}
              >
                {step.label}
              </span>

              {/* Connecting line */}
              {index !== steps.length - 1 && (
                <div
                  className={`ml-4 h-0.5 flex-1 transition-all duration-300 ${
                    step.status === 'complete'
                      ? 'bg-blue-600 dark:bg-blue-500'
                      : step.status === 'error'
                        ? 'bg-gradient-to-r from-red-500 to-gray-200 dark:to-zinc-700'
                        : 'bg-gray-200 dark:bg-zinc-700'
                  }`}
                />
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};
